import { defineStore } from 'pinia';
import { ref, unref, watch } from 'vue';
import {
  IntegrateFeaturedDashboardResponseV8Item,
  WidgetPackPatchRequestPackType,
  type WidgetPackRegisterRequestV8,
} from '@/api-client/metav8Over/model';
import { ViewPackInfo } from '@/dashboard/shared/types/widgets.types';
import {
  useGetV8DashboardsWidgetsPack,
  usePatchV8DashboardsWidgetsPack,
  usePostV8DashboardsWidgetsPack,
} from '@/api-client/metav8Over/queries/dashboard-widget-v8-controller/dashboard-widget-v8-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { convertToViewPackInfo } from '@/dashboard/features/dashboardCore/services/view-pack';
import { getV8DashboardsFeaturedDashboards } from '@/api-client/metav8Over/queries/integrated-dashboard-v8-controller/integrated-dashboard-v8-controller';
import { Category } from '../features/editToolbox/components/viewPackList/viewPackList.constant';
import { targetKindToCategory } from '../features/editToolbox/components/viewPackList/viewPackList.utils';
import { usePostV11TargetKind } from '@/api-client/target/queries/target-status-controller/target-status-controller';

export const useDashboardEditorViewStore = defineStore('dashboardDashboardEditorView', () => {
  const viewPackList = ref<ViewPackInfo[]>([]);
  const selectedTagList = ref<string[]>([]);

  // GET query for fetching view pack list
  const { error: viewPackError, refetch: refetchViewPackList } = useGetV8DashboardsWidgetsPack(
    {},
    {
      query: {
        enabled: false,
        select: (response) => response.data.data?.map(convertToViewPackInfo) ?? [],
      },
      request: {
        params: {
          frameName: FRAME_NAMES.DASHBOARD.VIEW_PACK_LIST,
        },
      },
    },
  );

  // Error handling
  watch(viewPackError, (error) => {
    if (error) {
      console.log(error);
    }
  });

  const fetchViewPackList = async () => {
    const result = await refetchViewPackList();
    const data = unref(result.data);
    if (data) {
      viewPackList.value = [...data];
    }
  };

  // PATCH & DELETE mutation for updating view pack
  const { mutateAsync: patchViewPackAsync } = usePatchV8DashboardsWidgetsPack({
    request: {
      params: {
        frameName: FRAME_NAMES.DASHBOARD.VIEW_PACK_UPDATE,
      },
    },
  });

  const updateViewPack = async (params: ViewPackInfo) => {
    await patchViewPackAsync({
      data: {
        ...params,
        deleted: false,
        value: JSON.stringify({
          titleOption: params.titleOption,
          widgetColor: params.widgetColor,
        }),
        widgetMappingId: params.widgetMappingId,
      },
    });
    await fetchViewPackList();
  };

  const deleteViewPack = async (id: number) => {
    const targetViewPack = viewPackList.value.find((viewPack) => +viewPack.packId === id);

    if (!targetViewPack) {
      return;
    }

    await patchViewPackAsync({
      data: {
        deleted: true,
        name: targetViewPack.name,
        packId: id,
        packType: targetViewPack.packType as WidgetPackPatchRequestPackType,
        widgetMappingId: targetViewPack.widgetMappingId,
        tag: '',
      },
    });
    await fetchViewPackList();
  };

  // POST mutation for registering view pack
  const { mutateAsync: postViewPackAsync } = usePostV8DashboardsWidgetsPack({
    request: {
      params: {
        frameName: FRAME_NAMES.DASHBOARD.VIEW_PACK_LIST,
      },
    },
  });

  const registerViewPack = async (params: WidgetPackRegisterRequestV8) => {
    await postViewPackAsync({
      data: params,
    });

    await fetchViewPackList();
  };

  const dashboardList = ref<IntegrateFeaturedDashboardResponseV8Item[]>([]);

  const fetchDashboardList = async () => {
    try {
      const { data } = await getV8DashboardsFeaturedDashboards(
        {
          featuredDashboardType: 'dashboard',
          showHidden: false,
        },
        {
          params: {
            frameName: FRAME_NAMES.DASHBOARD.FEATURED_DASHBOARD,
            isTimeout: false,
          },
        },
      );
      dashboardList.value = data?.data ?? [];
    } catch (error) {
      console.error('Failed to fetch dashboard list', error);
      dashboardList.value = [];
    }
  };

  const { mutateAsync: mutatePostV11TargetKind } = usePostV11TargetKind({
    request: {
      params: {
        frameName: FRAME_NAMES.TARGET.TARGET_KIND_LIST,
      },
    },
  });

  // 대상이 등록된 플랫폼 종류 목록
  const targetKindSet = ref<Set<Category>>(new Set());

  const fetchTargetKindList = async () => {
    mutatePostV11TargetKind(undefined, {
      onSuccess: (response) => {
        const newSet = new Set<Category>();
        response.data.data?.forEach((item) => {
          if (item.targetKind) {
            newSet.add(targetKindToCategory(item.targetKind));

            if (!item.subKind) {
              return;
            }

            newSet.add(targetKindToCategory(item.subKind));
          }
        });
        targetKindSet.value = newSet;
      },
      onError: (error) => {
        console.log(error);
      },
    });
  };

  const isTabPanelOpen = ref(false);

  const setIsTabPanelOpen = (value: boolean) => {
    isTabPanelOpen.value = value;
  };

  const resetState = () => {
    viewPackList.value = [];
    dashboardList.value = [];
    targetKindSet.value = new Set();
    selectedTagList.value = [];
    isTabPanelOpen.value = false;
  };

  return {
    viewPackList,
    selectedTagList,
    fetchViewPackList,
    registerViewPack,
    updateViewPack,
    deleteViewPack,

    dashboardList,
    fetchDashboardList,
    resetState,

    targetKindSet,
    fetchTargetKindList,

    isTabPanelOpen,
    setIsTabPanelOpen,
  };
});
